import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';
import useSubjectPersonGroupStore from '@/store/subject-person-group.store';
import { useTenantStore } from '@/store/tenant.store';
import { fetchDropdownOptionsFromReference } from '@/lib/reference-dropdown.helper.js';

export class SubjectPersonGroupIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch groups and update store
     */
    async fetchGroups(page = 1, pageSize = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc', columnFilters = {}) {
        const store = useSubjectPersonGroupStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearMessages();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                columnFilters
            });

            const { data, pagination, uniqueValues } = response;

            // Handle both items array and raw data
            const items = data?.items || data || [];
            store.setGroups(items);

            if (pagination) {
                store.setPaginationData({
                    currentPage: pagination.page || page,
                    totalPages: pagination.totalPages || 1,
                    totalItems: pagination.total || 0,
                    itemsPerPage: pagination.pageSize || pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create group
     */
    async createGroup(data) {
        const store = useSubjectPersonGroupStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const payload = {
                ...data,
                tenantId
            };

            const response = await this.service.create(payload);

            toast.success(i18next.t('kvkkSubjectPersonGroup:messages.createSuccess', {
                defaultValue: 'Data-subject group created',
            }));

            if (store.closeForm) store.closeForm();
            await this.fetchGroups(); // Refresh list

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update group
     */
    async updateGroup(id, data) {
        const store = useSubjectPersonGroupStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('kvkkSubjectPersonGroup:messages.updateSuccess', {
                defaultValue: 'Data-subject group updated',
            }));

            if (store.closeForm) store.closeForm();
            await this.fetchGroups(); // Refresh list

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            store.setError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete group
     */
    async deleteGroup(id) {
        const store = useSubjectPersonGroupStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('kvkkSubjectPersonGroup:messages.deleteSuccess', {
                defaultValue: 'Data-subject group deleted',
            }));

            if (store.closeDeleteDialog) store.closeDeleteDialog();
            await this.fetchGroups(); // Refresh list

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Bulk delete groups
     */
    async bulkDeleteGroups(ids) {
        const store = useSubjectPersonGroupStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            await this.service.bulkDelete(ids);

            toast.success(i18next.t('kvkkSubjectPersonGroup:messages.bulkDeleteSuccess', {
                count: ids.length,
                defaultValue: `${ids.length} data-subject groups deleted`,
            }));
            await this.fetchGroups(); // Refresh list

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    async getGroupById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /**
     * Fetch dropdown selection data
     */
    async fetchDropdownData() {
        try {
            return await fetchDropdownOptionsFromReference('subjectPersonGroup', async () => {
                const response = await this.service.getDropdownParams();
                return { success: true, data: response.data || [] };
            });
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
